import apiClient from '../axios.js';

export const couponService = {
    // ==================== 優惠券模板管理 ====================

    /**
     * 上傳票券圖片
     * @param {File} file - 圖片檔案
     * @returns {Promise<Object>} 上傳結果 { fileUrl, fileName, ... }
     */
    uploadTemplateImage: async (file) => {
        const formData = new FormData();
        formData.append('file', file);
        const response = await apiClient.post('/upload', formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
        });
        return response;
    },

    /**
     * 查詢優惠券模板列表
     * @param {Object} params - 查詢參數
     * @returns {Promise<Object>} 優惠券模板列表
     */
    getTemplates: async (params = {}) => {
        const response = await apiClient.get('/admin/coupons/templates', { params });
        return response;
    },

    /**
     * 查詢優惠券模板詳情
     * @param {string} id - 模板 ID
     * @returns {Promise<Object>} 優惠券模板詳情
     */
    getTemplate: async (id) => {
        const response = await apiClient.get(`/admin/coupons/templates/${id}`);
        return response;
    },

    /**
     * 查詢優惠券模板的發放記錄
     * @param {string} id - 模板 ID
     * @param {Object} params - 查詢參數
     * @returns {Promise<Object>} 發放記錄列表
     */
    getTemplateIssues: async (id, params = {}) => {
        const response = await apiClient.get(`/admin/coupons/templates/${id}/issues`, { params });
        return response;
    },

    /**
     * 建立優惠券模板
     * @param {Object} data - 模板資料
     * @returns {Promise<Object>} 建立的優惠券模板
     */
    createTemplate: async (data) => {
        const response = await apiClient.post('/admin/coupons/templates', data);
        return response;
    },

    /**
     * 更新優惠券模板
     * @param {string} id - 模板 ID
     * @param {Object} data - 更新的模板資料
     * @returns {Promise<Object>} 更新後的優惠券模板
     */
    updateTemplate: async (id, data) => {
        const response = await apiClient.put(`/admin/coupons/templates/${id}`, data);
        return response;
    },

    /**
     * 檢查優惠券模板發送狀態
     * @param {string} id - 模板 ID
     * @returns {Promise<Object>} 發送狀態
     */
    checkTemplateIssuanceStatus: async (id) => {
        const response = await apiClient.get(`/admin/coupons/templates/${id}/issuance-status`);
        return response;
    },

    /**
     * 查詢優惠券模板被引用的行銷流程與工具
     * @param {string} id - 模板 ID
     * @returns {Promise<Object>} 引用清單
     */
    getTemplateReferences: async (id) => {
        const response = await apiClient.get(`/admin/coupons/templates/${id}/references`);
        return response;
    },

    /**
     * 刪除優惠券模板
     * @param {string} id - 模板 ID
     * @param {boolean} deleteIssues - 是否同時刪除已發送的優惠券
     * @returns {Promise<Object>} 刪除結果
     */
    deleteTemplate: async (id, deleteIssues = false) => {
        const response = await apiClient.delete(`/admin/coupons/templates/${id}`, {
            params: { deleteIssues }
        });
        return response;
    },

    /**
     * 取得優惠券模板下拉選單
     * @param {Object} params - 查詢參數
     * @returns {Promise<Object>} 優惠券模板下拉選單
     */
    getTemplatesDropdown: async (params = {}) => {
        const response = await apiClient.get('/admin/coupons/templates/dropdown', { params });
        return response;
    },

    // ==================== 優惠券發放管理 ====================
    
    /**
     * 查詢所有優惠券發放記錄
     * @param {Object} params - 查詢參數
     * @returns {Promise<Object>} 優惠券發放記錄列表
     */
    getIssues: async (params = {}) => {
        const response = await apiClient.get('/admin/coupons/issues', { params });
        return response;
    },
    
    /**
     * 匯出用券名單（依券模板或批量發放活動）
     * @param {Object} params - 匯出參數
     * @param {string} [params.templateId] - 券模板 ID（與 batchIssuanceId 至少擇一）
     * @param {string} [params.batchIssuanceId] - 批量發放活動 ID
     * @param {string} [params.status] - 券狀態篩選
     * @param {string} [params.mode] - 匯出粒度：'detail'（逐張券）| 'summary'（逐會員彙總）
     * @param {string} [params.format] - 檔案格式：'csv' | 'excel'
     * @returns {Promise<Blob>} 匯出檔案 Blob
     */
    exportIssues: async (params = {}) => {
        const response = await apiClient.get('/admin/coupons/issues/export', {
            params,
            responseType: 'blob'
        });
        return response;
    },

    /**
     * 查詢會員的優惠券列表
     * @param {string} memberId - 會員 ID
     * @param {Object} params - 查詢參數
     * @returns {Promise<Object>} 優惠券列表
     */
    getCouponsByMember: async (memberId, params = {}) => {
        const response = await apiClient.get(`/coupons/member/${memberId}`, { params });
        return response;
    },

    /**
     * 依 LINE User ID 查詢票券（LINE 好友詳情頁使用）
     * @param {string} lineUserId - LINE User ID
     * @param {Object} params - 查詢參數
     * @returns {Promise<Object>} 票券列表
     */
    getCouponsByLineUser: async (lineUserId, params = {}) => {
        const response = await apiClient.get(`/admin/coupons/issues/by-line-user/${encodeURIComponent(lineUserId)}`, { params });
        return response;
    },

    /**
     * 查詢會員可用的優惠券
     * @param {string} memberId - 會員 ID
     * @param {Object} params - 查詢參數
     * @returns {Promise<Object>} 可用優惠券列表
     */
    getAvailableCoupons: async (memberId, params = {}) => {
        const response = await apiClient.get(`/coupons/available/${memberId}`, { params });
        return response;
    },

    /**
     * 根據 ID 查詢優惠券詳情
     * @param {string} id - 優惠券 ID
     * @returns {Promise<Object>} 優惠券詳情
     */
    getCouponById: async (id) => {
        const response = await apiClient.get(`/coupons/issues/${id}`);
        return response;
    },

    /**
     * 查詢優惠券詳情（根據代碼）
     * @param {string} couponCode - 優惠券代碼
     * @returns {Promise<Object>} 優惠券詳情
     */
    getCoupon: async (couponCode) => {
        const response = await apiClient.get(`/coupons/${couponCode}/detail`);
        return response;
    },

    /**
     * 發放優惠券給會員
     * @param {Object} data - 發放資料
     * @returns {Promise<Object>} 發放結果
     */
    issueCoupon: async (data) => {
        const response = await apiClient.post('/coupons/issue', data);
        return response;
    },

    /**
     * 驗證優惠券
     * @param {string} couponCode - 優惠券代碼
     * @returns {Promise<Object>} 驗證結果
     */
    validateCoupon: async (couponCode) => {
        const response = await apiClient.post('/coupons/validate', { couponCode });
        return response;
    },

    /**
     * 核銷優惠券
     * @param {Object} data - 核銷資料
     * @returns {Promise<Object>} 核銷結果
     */
    redeemCoupon: async (data) => {
        const response = await apiClient.post('/coupons/redeem/manual', data);
        return response;
    },

    /**
     * 取消/作廢優惠券
     * @param {string} couponId - 優惠券 ID
     * @param {string} reason - 取消原因
     * @returns {Promise<Object>} 取消結果
     */
    cancelCoupon: async (couponId, reason) => {
        const response = await apiClient.put(`/coupons/${couponId}/cancel`, { cancelReason: reason });
        return response;
    },

    /**
     * 預覽 LINE 好友派券對象數量
     * @param {Object} data
     * @param {string} data.friendTargetMode - 'all_friends' | 'line_tags'
     * @param {Array<string>} [data.lineTagNames] - LINE 標籤名稱陣列
     * @param {string} [data.lineTagMatchType] - 'any' | 'all'
     * @returns {Promise<Object>} { count: number }
     */
    previewFriendCount: async (data) => {
        const response = await apiClient.post('/admin/coupons/issues/batch/preview-friends', data);
        return response;
    },

    /**
     * 批次發送優惠券
     * @param {Object} data - 批次發送資料
     * @param {string} data.templateId - 優惠券模板 ID
     * @param {string} data.targetAudience - 發放對象類型：'member' | 'friend'
     * @param {Array<string>} [data.memberIds] - 會員 ID 陣列（member 模式必填）
     * @param {boolean} [data.sendLineNotification] - 是否同步發送 LINE 推播（member 模式）
     * @param {Array<string>} [data.lineUserIds] - LINE User ID 陣列（friend 模式，指定好友）
     * @param {Array<string>} [data.lineTagNames] - LINE 標籤名稱陣列（friend 模式，依標籤篩選）
     * @param {number} data.quantity - 每人發放數量（1-10）
     * @param {string} data.reason - 發放原因（選填）
     * @returns {Promise<Object>} 批次發送結果
     */
    batchIssueCoupons: async (data) => {
        const response = await apiClient.post('/admin/coupons/issues/batch', data);
        return response;
    },

    /**
     * 上傳檔案（用於 CSV 備份）
     * @param {FormData} formData - 含 file 欄位的 FormData
     * @returns {Promise<Object>} 上傳結果（含 fileUrl）
     */
    uploadFile: async (formData) => {
        const response = await apiClient.post('/upload', formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
        });
        return response;
    },

    // ==================== 批次發放優惠券管理 ====================

    /**
     * 查詢批次發放優惠券列表
     * @param {Object} params - 查詢參數
     * @returns {Promise<Object>} 批次發放優惠券列表
     */
    getBatchIssuances: async (params = {}) => {
        const response = await apiClient.get('/admin/coupons/batch-issuances', { params });
        return response;
    },

    /**
     * 查詢批次發放優惠券詳情
     * @param {string} batchId - 批次 ID
     * @returns {Promise<Object>} 批次發放優惠券詳情
     */
    getBatchIssuance: async (batchId) => {
        const response = await apiClient.get(`/admin/coupons/batch-issuances/${batchId}`);
        return response;
    },

    /**
     * 查詢批次發送明細列表
     * @param {string} batchId - 批次 ID
     * @param {Object} params - 查詢參數
     * @returns {Promise<Object>} 明細列表
     */
    getBatchIssuanceDetails: async (batchId, params = {}) => {
        const response = await apiClient.get(`/admin/coupons/batch-issuances/${batchId}/details`, { params });
        return response;
    },

    /**
     * 檢查批次發送狀態
     * @param {string} batchId - 批次 ID
     * @returns {Promise<Object>} 批次發送狀態
     */
    checkBatchIssuanceStatus: async (batchId) => {
        const response = await apiClient.get(`/admin/coupons/batch-issuances/${batchId}/issuance-status`);
        return response;
    },

    /**
     * 刪除批次作業紀錄（不影響已發出的票券）
     * @param {string} batchId - 批次 ID
     * @returns {Promise<Object>} 刪除結果
     */
    deleteBatchIssuance: async (batchId) => {
        const response = await apiClient.delete(`/admin/coupons/batch-issuances/${batchId}`);
        return response;
    },

    /**
     * 撤銷該批次已發出的票券（軟刪除，保留批次紀錄）
     * @param {string} batchId - 批次 ID
     * @returns {Promise<Object>} 撤銷結果
     */
    revokeIssuedCoupons: async (batchId) => {
        const response = await apiClient.post(`/admin/coupons/batch-issuances/${batchId}/revoke-coupons`);
        return response;
    },

    /**
     * 查詢批次發放的優惠券記錄
     * @param {string} batchId - 批次 ID
     * @param {string} memberId - 會員 ID
     * @returns {Promise<Object>} 優惠券記錄列表
     */
    getCouponsByBatchAndMember: async (batchId, memberId) => {
        const response = await apiClient.get(`/admin/coupons/issues/by-batch/${batchId}/${memberId}`);
        return response;
    },

    /**
     * 刪除優惠券發放記錄
     * @param {string} couponId - 優惠券 ID
     * @returns {Promise<Object>} 刪除結果
     */
    deleteCouponIssue: async (couponId, deleteReason = '') => {
        const response = await apiClient.delete(`/admin/coupons/issues/${couponId}`, {
            data: { deleteReason }
        });
        return response;
    }
};
